import { useEffect, useState, useCallback } from 'react';

/**
 * CookieConsent — banner GDPR conforme.
 * - Nessun cookie/analytics caricato prima del consenso esplicito.
 * - Pulsanti chiari "Accetta" / "Rifiuta" (no dark pattern).
 * - Persiste la scelta in localStorage.
 * - Al consenso, carica PostHog lato client.
 *
 * Riferimento normativo: Direttiva ePrivacy 2002/58/CE + GDPR art. 6/7,
 * Provv. Garante Privacy IT n. 231/2021.
 */
const STORAGE_KEY = 'cg_cookie_consent_v1';
const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';

function loadPostHog() {
  if (window.posthog && window.posthog.__loaded) return;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV || ((window.posthog = e), (e._i = []), (e.init = function (i, s, a) {
      function g(t, e) { var o = e.split('.'); 2 == o.length && ((t = t[o[0]]), (e = o[1])), (t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }); }
      ((p = t.createElement('script')).type = 'text/javascript'),
        (p.crossOrigin = 'anonymous'),
        (p.async = !0),
        (p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js'),
        (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
      var u = e;
      for (void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
        u.people = u.people || [],
        u.toString = function (t) { var e = 'posthog'; return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e; },
        u.people.toString = function () { return u.toString(1) + '.people (stub)'; },
        o = 'init capture identify alias reset opt_in_capturing opt_out_capturing'.split(' '),
        n = 0; n < o.length; n++) g(u, o[n]);
      e._i.push([i, s, a]);
    }), (e.__SV = 1));
  })(document, window.posthog || []);
  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
  });
  window.posthog.__loaded = true;
  /* eslint-enable */
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'accepted') {
      loadPostHog();
    } else if (!stored) {
      // Mostra il banner dopo un breve delay per non disturbare LCP
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setVisible(false);
    loadPostHog();
  }, []);

  const decline = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[9000] animate-slide-up"
      data-testid="cookie-consent"
    >
      <div className="bg-ink text-ivory border border-ivory/10 rounded-md p-6 shadow-lg">
        <h2 id="cookie-consent-title" className="font-display text-h4 text-ivory mb-2">
          Rispettiamo la tua privacy
        </h2>
        <p id="cookie-consent-desc" className="text-small text-ivory/70 leading-relaxed mb-5">
          Usiamo cookie tecnici essenziali e, previo tuo consenso, cookie analitici
          (PostHog) per migliorare l'esperienza di navigazione. Nessun dato viene
          condiviso con terze parti pubblicitarie.
          {' '}
          <a href="/privacy" className="underline text-ivory hover:text-brass">
            Maggiori informazioni
          </a>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={accept}
            className="flex-1 h-11 px-6 bg-ivory text-ink rounded-sm text-small font-medium hover:bg-brass hover:text-ink transition-colors duration-hover"
            data-testid="cookie-accept"
          >
            Accetta
          </button>
          <button
            onClick={decline}
            className="flex-1 h-11 px-6 bg-transparent text-ivory border border-ivory/30 rounded-sm text-small font-medium hover:border-ivory transition-colors duration-hover"
            data-testid="cookie-decline"
          >
            Rifiuta
          </button>
        </div>
      </div>
    </div>
  );
}
